import React, { useState, useRef, useEffect, useCallback } from 'react';
import { WorkflowVariable } from '../types/workflow';
import { StorageFile, listStorageFiles, uploadFiles, downloadFile, deleteFile } from '../services/storageService';

export interface OutputLog {
    timestamp: string;
    level: 'log' | 'error' | 'warn';
    message: string;
    nodeLabel?: string;
}

interface BottomPanelProps {
    variables: WorkflowVariable[];
    onChange: (variables: WorkflowVariable[]) => void;
    runtimeVariables?: Record<string, any>;
    onRuntimeVariablesUpdate?: (vars: Record<string, any>) => void;
    outputLogs?: OutputLog[];
    onClearLogs?: () => void;
    workflowId?: string;
}

function detectType(value: any): { label: string; color: string } {
    if (Array.isArray(value)) return { label: `array[${value.length}]`, color: '#9C27B0' };
    if (value !== null && typeof value === 'object') return { label: 'object', color: '#00695C' };
    if (typeof value !== 'string') return { label: typeof value, color: '#aaa' };
    if (value.trim() === '') return { label: 'empty', color: '#aaa' };
    if (value.trim() === 'true' || value.trim() === 'false') return { label: 'boolean', color: '#FF9800' };
    if (!isNaN(Number(value.trim()))) return { label: 'number', color: '#2196F3' };
    if (value.trim().startsWith('<') && value.trim().endsWith('>')) return { label: 'html', color: '#E91E63' };
    try {
        const parsed = JSON.parse(value.trim());
        if (Array.isArray(parsed)) return { label: `array[${parsed.length}]`, color: '#9C27B0' };
        if (typeof parsed === 'object' && parsed !== null) return { label: 'object', color: '#00695C' };
    } catch {}
    return { label: 'string', color: '#4CAF50' };
}

const BottomPanel: React.FC<BottomPanelProps> = ({
    variables, onChange, runtimeVariables = {}, onRuntimeVariablesUpdate, outputLogs = [], onClearLogs, workflowId
}) => {
    const [activeTab, setActiveTab] = useState<'variables' | 'output' | 'storage'>('variables');
    const [expanded, setExpanded] = useState(false);
    const logEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Storage state
    const [storageFiles, setStorageFiles] = useState<StorageFile[]>([]);
    const [storageLoading, setStorageLoading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const refreshStorage = useCallback(async () => {
        if (!workflowId) return;
        setStorageLoading(true);
        try {
            const files = await listStorageFiles(workflowId);
            setStorageFiles(files);
        } catch (err) {
            console.error('Failed to list storage:', err);
        } finally {
            setStorageLoading(false);
        }
    }, [workflowId]);

    useEffect(() => {
        if (activeTab === 'storage' && expanded && workflowId) refreshStorage();
    }, [activeTab, expanded, workflowId, refreshStorage]);

    const handleUpload = async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0 || !workflowId) return;
        try {
            await uploadFiles(workflowId, Array.from(fileList));
            await refreshStorage();
        } catch (err) {
            console.error('Upload failed:', err);
        }
    };

    const handleDelete = async (filename: string) => {
        if (!workflowId) return;
        try {
            await deleteFile(workflowId, filename);
            setStorageFiles(prev => prev.filter(f => f.name !== filename));
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const designNames = new Set(variables.map(v => v.name));
    const runtimeOnlyEntries = Object.entries(runtimeVariables)
        .filter(([k]) => !designNames.has(k) && !k.startsWith('__'));
    const totalVarCount = variables.length + runtimeOnlyEntries.length;

    useEffect(() => {
        if (activeTab === 'output' && expanded) {
            logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [outputLogs, activeTab, expanded]);

    const prevLogCountRef = useRef(outputLogs.length);
    useEffect(() => {
        if (outputLogs.length > prevLogCountRef.current) {
            prevLogCountRef.current = outputLogs.length;
            setActiveTab('output');
            setExpanded(true);
        }
    }, [outputLogs.length]);

    const addVariable = () => {
        onChange([...variables, { name: `var_${variables.length + 1}`, value: '' }]);
        setExpanded(true);
        setActiveTab('variables');
    };

    const updateVariable = (index: number, field: 'name' | 'value', val: string) => {
        onChange(variables.map((v, i) => i === index ? { ...v, [field]: val } : v));
    };

    const deleteVariable = (index: number) => {
        const deletedName = variables[index]?.name;
        onChange(variables.filter((_, i) => i !== index));
        // Also remove from runtime variables (and its __locator_ sibling)
        if (deletedName && onRuntimeVariablesUpdate) {
            const updated = { ...runtimeVariables };
            delete updated[deletedName];
            delete updated[`__locator_${deletedName}`];
            onRuntimeVariablesUpdate(updated);
        }
    };

    const levelColor = (level: OutputLog['level']) => {
        if (level === 'error') return '#ff5252';
        if (level === 'warn') return '#FFB300';
        return '#69f0ae';
    };

    const levelBg = (level: OutputLog['level']) => {
        if (level === 'error') return 'rgba(255,82,82,0.08)';
        if (level === 'warn') return 'rgba(255,179,0,0.08)';
        return 'transparent';
    };

    const unreadLogs = outputLogs.length > 0 && !(activeTab === 'output' && expanded);

    return (
        <div style={{ borderTop: '2px solid #007acc', background: '#fafafa', flexShrink: 0, display: 'flex', flexDirection: 'column', maxHeight: expanded ? 280 : 'auto' }}>
            {/* Tab bar */}
            <div style={{ display: 'flex', alignItems: 'center', background: '#f0f0f0', borderBottom: '1px solid #ddd', flexShrink: 0 }}>
                <button
                    onClick={() => { setActiveTab('variables'); setExpanded(true); }}
                    style={{ padding: '7px 18px', border: 'none', borderBottom: activeTab === 'variables' && expanded ? '2px solid #007acc' : '2px solid transparent', background: 'transparent', cursor: 'pointer', fontWeight: activeTab === 'variables' ? 'bold' : 'normal', color: activeTab === 'variables' ? '#007acc' : '#555', fontSize: 13 }}
                >
                    📦 Variables
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#aaa' }}>
                        {variables.length}
                        {runtimeOnlyEntries.length > 0 && <span style={{ color: '#e65100' }}> +{runtimeOnlyEntries.length}</span>}
                    </span>
                </button>
                <button
                    onClick={() => { setActiveTab('output'); setExpanded(true); }}
                    style={{ padding: '7px 18px', border: 'none', borderBottom: activeTab === 'output' && expanded ? '2px solid #37474F' : '2px solid transparent', background: 'transparent', cursor: 'pointer', fontWeight: activeTab === 'output' ? 'bold' : 'normal', color: activeTab === 'output' ? '#37474F' : '#555', fontSize: 13, position: 'relative' }}
                >
                    💻 Output
                    {outputLogs.length > 0 && (
                        <span style={{ marginLeft: 6, fontSize: 10, background: unreadLogs ? '#f44336' : '#607D8B', color: 'white', padding: '1px 6px', borderRadius: 10, fontWeight: 'bold' }}>
                            {outputLogs.length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => { setActiveTab('storage'); setExpanded(true); }}
                    style={{ padding: '7px 18px', border: 'none', borderBottom: activeTab === 'storage' && expanded ? '2px solid #6d4c41' : '2px solid transparent', background: 'transparent', cursor: 'pointer', fontWeight: activeTab === 'storage' ? 'bold' : 'normal', color: activeTab === 'storage' ? '#6d4c41' : '#555', fontSize: 13 }}
                >
                    📁 Storage
                    {storageFiles.length > 0 && (
                        <span style={{ marginLeft: 6, fontSize: 10, background: '#6d4c41', color: 'white', padding: '1px 6px', borderRadius: 10, fontWeight: 'bold' }}>
                            {storageFiles.length}
                        </span>
                    )}
                </button>
                <div style={{ flex: 1 }} />
                {activeTab === 'variables' && (
                    <button onClick={(e) => { e.stopPropagation(); addVariable(); }} style={{ padding: '3px 10px', background: '#007acc', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold', marginRight: 8 }}>
                        + Add Variable
                    </button>
                )}
                {activeTab === 'output' && outputLogs.length > 0 && (
                    <button onClick={onClearLogs} style={{ padding: '3px 10px', background: '#607D8B', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold', marginRight: 8 }}>
                        🗑 Clear
                    </button>
                )}
                {activeTab === 'storage' && (
                    <>
                        <button onClick={() => fileInputRef.current?.click()} style={{ padding: '3px 10px', background: '#6d4c41', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold', marginRight: 4 }}>
                            📤 Upload
                        </button>
                        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => { handleUpload(e.target.files); e.target.value = ''; }} />
                        <button onClick={refreshStorage} style={{ padding: '3px 10px', background: '#78909C', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold', marginRight: 8 }}>
                           🔄
                        </button>
                    </>
                )}
                <button onClick={() => setExpanded(e => !e)} style={{ padding: '4px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: '#555', marginRight: 4 }} title={expanded ? 'Collapse' : 'Expand'}>
                    {expanded ? '▼' : '▲'}
                </button>
            </div>

            {expanded && (
                <div style={{ flex: 1, overflowY: 'auto', maxHeight: 240 }}>
                    {activeTab === 'variables' && (
                        <div style={{ padding: '8px 16px' }}>
                            {totalVarCount === 0 ? (
                                <div style={{ color: '#aaa', fontSize: 13, padding: '10px 0', textAlign: 'center' }}>
                                    No variables yet. Click <strong>+ Add Variable</strong> to create one.
                                </div>
                            ) : (
                                <table className="variables-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 30 }}>#</th>
                                            <th>Name</th>
                                            <th>Value</th>
                                            <th style={{ width: 70 }}>Type</th>
                                            <th style={{ width: 36 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {variables.map((v, i) => {
                                            const type = detectType(v.value);
                                            const runtimeValue = runtimeVariables[v.name];
                                            const hasRuntimeOverride = runtimeValue !== undefined && runtimeValue !== v.value.replace(/^["']|["']$/g, '');
                                            return (
                                                <tr key={`design_${i}`}>
                                                    <td style={{ color: '#aaa', fontSize: 12, textAlign: 'center' }}>{i + 1}</td>
                                                    <td><input className="var-input" value={v.name} onChange={(e) => updateVariable(i, 'name', e.target.value)} placeholder="variable_name" spellCheck={false} /></td>
                                                    <td>
                                                        <input className="var-input var-value-input" value={v.value} onChange={(e) => updateVariable(i, 'value', e.target.value)} placeholder='"string" / 123 / true' spellCheck={false} style={{ fontFamily: 'monospace' }} />
                                                        {hasRuntimeOverride && (
                                                            <div style={{ marginTop: 2, fontSize: 10, color: '#e65100', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                <span>⚡ runtime:</span>
                                                                <span style={{ background: '#fff3e0', padding: '1px 5px', borderRadius: 3, border: '1px solid #ffcc80', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={typeof runtimeValue === 'object' && runtimeValue !== null ? JSON.stringify(runtimeValue) : String(runtimeValue)}>
                                                                    {typeof runtimeValue === 'object' && runtimeValue !== null ? JSON.stringify(runtimeValue) : String(runtimeValue)}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td><span style={{ background: hasRuntimeOverride ? '#e65100' : type.color, color: 'white', padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 'bold', whiteSpace: 'nowrap' }}>{hasRuntimeOverride ? '⚡ ' : ''}{type.label}</span></td>
                                                    <td><button onClick={() => deleteVariable(i)} style={{ background: 'none', border: 'none', color: '#f44336', cursor: 'pointer', fontSize: 16, padding: '2px 4px', lineHeight: 1 }}>×</button></td>
                                                </tr>
                                            );
                                        })}
                                        {runtimeOnlyEntries.length > 0 && (
                                            <tr>
                                                <td colSpan={5} style={{ padding: '6px 8px 2px', fontSize: 10, fontWeight: 'bold', color: '#e65100', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #ffe0b2', background: '#fff8f0' }}>
                                                    ⚡ Runtime Variables (read-only, not saved)
                                                </td>
                                            </tr>
                                        )}
                                        {runtimeOnlyEntries.map(([k, v]) => {
                                            const type = detectType(v);
                                            const displayValue = (() => {
                                                if (Array.isArray(v)) return `[${(v as any[]).length} items]`;
                                                if (typeof v === 'object' && v !== null) return JSON.stringify(v);
                                                if (typeof v === 'string') {
                                                    try {
                                                        const p = JSON.parse(v);
                                                        if (Array.isArray(p)) return `[${p.length} items]`;
                                                    } catch {}
                                                }
                                                return String(v);
                                            })();
                                            return (
                                                <tr key={`runtime_${k}`} style={{ background: '#fff8f0' }}>
                                                    <td style={{ color: '#e65100', fontSize: 12, textAlign: 'center' }}>⚡</td>
                                                    <td><input className="var-input" value={k} readOnly style={{ fontFamily: 'monospace', background: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80', cursor: 'default' }} /></td>
                                                    <td><input className="var-input var-value-input" value={displayValue} readOnly style={{ fontFamily: 'monospace', background: '#fff3e0', color: '#555', border: '1px solid #ffcc80', cursor: 'default' }} /></td>
                                                    <td><span style={{ background: type.color, color: 'white', padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 'bold', whiteSpace: 'nowrap' }}>{type.label}</span></td>
                                                    <td />
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {activeTab === 'output' && (
                        <div style={{ background: '#1e1e1e', minHeight: 80, fontFamily: 'monospace', fontSize: 12 }}>
                            {outputLogs.length === 0 ? (
                                <div style={{ color: '#555', padding: '20px', textAlign: 'center', fontSize: 12 }}>
                                    No output yet. Run a Code node with <code style={{ color: '#888' }}>console.log()</code> to see output here.
                                </div>
                            ) : (
                                outputLogs.map((log, i) => (
                                    <div key={i} style={{ padding: '3px 12px', borderBottom: '1px solid #2a2a2a', background: levelBg(log.level), display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                        <span style={{ color: '#555', fontSize: 10, whiteSpace: 'nowrap', paddingTop: 2 }}>{log.timestamp}</span>
                                        {log.nodeLabel && <span style={{ color: '#888', fontSize: 10, whiteSpace: 'nowrap', paddingTop: 2, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>[{log.nodeLabel}]</span>}
                                        <span style={{ color: levelColor(log.level), flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{log.message}</span>
                                    </div>
                                ))
                            )}
                            <div ref={logEndRef} />
                        </div>
                    )}

                    {activeTab === 'storage' && (
                        <div
                            style={{ padding: 0, minHeight: 80 }}
                            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
                        >
                            {!workflowId ? (
                                <div style={{ color: '#aaa', padding: 20, textAlign: 'center', fontSize: 13 }}>
                                    Save the workflow first to enable storage.
                                </div>
                            ) : storageLoading ? (
                                <div style={{ color: '#aaa', padding: 20, textAlign: 'center', fontSize: 13 }}>Loading...</div>
                            ) : storageFiles.length === 0 ? (
                                <div style={{ color: '#aaa', padding: 20, textAlign: 'center', fontSize: 13, border: dragOver ? '2px dashed #6d4c41' : '2px dashed transparent', borderRadius: 8, margin: 8, transition: 'border 0.2s' }}>
                                    No files yet. Drag & drop files here or click <strong>📤 Upload</strong>.
                                    <br /><span style={{ fontSize: 11 }}>Browser downloads during execution will also appear here.</span>
                                </div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                        <tr style={{ background: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                                            <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#555' }}>File</th>
                                            <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, color: '#555', width: 80 }}>Size</th>
                                            <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, color: '#555', width: 140 }}>Modified</th>
                                            <th style={{ width: 80 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {storageFiles.map(f => (
                                            <tr key={f.name} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '5px 12px', fontFamily: 'monospace', color: '#333' }}>📄 {f.name}</td>
                                                <td style={{ padding: '5px 12px', textAlign: 'right', color: '#888' }}>{formatSize(f.size)}</td>
                                                <td style={{ padding: '5px 12px', textAlign: 'right', color: '#888', fontSize: 11 }}>{new Date(f.modified).toLocaleString()}</td>
                                                <td style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    <button onClick={() => downloadFile(workflowId, f.name)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '2px 4px' }} title="Download">⬇️</button>
                                                    <button onClick={() => handleDelete(f.name)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '2px 4px', color: '#f44336' }} title="Delete">🗑️</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            {dragOver && (
                                <div style={{ position: 'absolute', inset: 0, background: 'rgba(109,76,65,0.12)', border: '3px dashed #6d4c41', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#6d4c41', fontWeight: 'bold', pointerEvents: 'none' }}>
                                    Drop files to upload
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BottomPanel;
